<template>
  <div v-show="content" :class="[$style.cms, {[$style.cmsVersion2]: version2}]" v-html="processedContent" />
</template>

<script>
  /**
   * This component was created for CKEditor5, to support media embedding.
   * The following sources were the basis of the core functionality:
   * @source https://github.com/ckeditor/ckeditor5-media-embed/blob/master/src/mediaembedediting.js
   * @source https://github.com/ckeditor/ckeditor5-media-embed/blob/master/src/mediaregistry.js
   */

  const OEMBED_PROVIDER_TYPES = [{
    name: 'dailymotion',
    url: /^dailymotion\.com\/video\/(\w+)/,
    html: (match) => {
      const id = match[1];

      return `<div class="video"><iframe src="//www.dailymotion.com/embed/video/${id}" frameborder="0" allowfullscreen></iframe></div>`;
    },
  }, {
    name: 'spotify',
    url: [
      /^open\.spotify\.com\/(artist\/\w+)/,
      /^open\.spotify\.com\/(album\/\w+)/,
      /^open\.spotify\.com\/(track\/\w+)/,
    ],
    html: (match) => {
      const id = match[1];

      return `<div class="audio spotify"><iframe src="//open.spotify.com/embed/${id}" frameborder="0" allowtransparency="true" allow="encrypted-media"></iframe></div>`;
    },
  }, {
    name: 'youtube',
    url: [
      /^(?:m\.)?youtube\.com\/watch\?v=([\w-]+)/,
      /^(?:m\.)?youtube\.com\/v\/([\w-]+)/,
      /^youtube\.com\/embed\/([\w-]+)/,
      /^youtu\.be\/([\w-]+)/,
    ],
    html: (match) => {
      const id = match[1];

      return `<div class="video"><iframe src="//www.youtube.com/embed/${id}" frameborder="0" allow="encrypted-media" allowfullscreen></iframe></div>`;
    },
  }, {
    name: 'vimeo',
    url: [
      /^vimeo\.com\/(\d+)/,
      /^vimeo\.com\/[^/]+\/[^/]+\/video\/(\d+)/,
      /^vimeo\.com\/album\/[^/]+\/video\/(\d+)/,
      /^vimeo\.com\/channels\/[^/]+\/(\d+)/,
      /^vimeo\.com\/groups\/[^/]+\/videos\/(\d+)/,
      /^vimeo\.com\/ondemand\/[^/]+\/(\d+)/,
      /^player\.vimeo\.com\/video\/(\d+)/,
    ],
    html: (match) => {
      const id = match[1];

      return `<div class="video"><iframe src="//player.vimeo.com/video/${id}" frameborder="0" webkitallowfullscreen mozallowfullscreen allowfullscreen></iframe></div>`;
    },
  }, {
    name: 'instagram',
    url: /^instagram\.com\/p\/(\w+)/,
  }, {
    name: 'twitter',
    url: /^twitter\.com/,
  }, {
    name: 'googleMaps',
    url: /^google\.com\/maps/,
  }, {
    name: 'flickr',
    url: /^flickr\.com/,
  }, {
    name: 'facebook',
    url: /^facebook\.com/,
  }];

  const OEMBED_CONTENT_REGEX = /(<oembed url=")([[^\-\]_.~!*'();:@&=+$,/?%#A-z0-9]+)("><\/oembed>)/g;

  function getUrlMatches (url, pattern) {
    let match = url.match(pattern);

    if (match) {
      return match;
    }

    let rawUrl = url.replace(/^https?:\/\//, '');
    match = rawUrl.match(pattern);

    if (match) {
      return match;
    }

    rawUrl = rawUrl.replace(/^www\./, '');
    match = rawUrl.match(pattern);

    if (match) {
      return match;
    }

    return null;
  }

  function resolveUrl (url) {
    for (const definition of OEMBED_PROVIDER_TYPES) {
      let pattern = definition.url;

      if (!Array.isArray(pattern)) {
        pattern = [pattern];
      }

      for (const subPattern of pattern) {
        const match = getUrlMatches(url, subPattern);

        if (match) {
          if (definition.html) {
            return definition.html(match);
          } else {
            return `<a href="${url}" target="_blank" class="embed-generic ${definition.name}"><span>${url}</span></a>`;
          }
        }
      }
    }
  }

  export default {
    name: 'LvCms',
    props: {
      content: {
        type: String,
        required: true,
      },
      version2: {
        type: Boolean,
        default: false,
      },
    },
    computed: {
      processedContent () {
        return this.content.replace(OEMBED_CONTENT_REGEX, (match, start, url) => {
          return resolveUrl(url) || '';
        });
      },
    },
  };
</script>

<style module lang="scss" rel="stylesheet/scss">
$lv-cms-heading-display: $heading-display !default;

$lv-cms-heading-1-font-size: $heading-1-font-size !default;
$lv-cms-heading-1-line-height: $heading-1-line-height !default;
$lv-cms-heading-1-font-weight: $heading-1-font-weight !default;
$lv-cms-heading-1-margin: $heading-1-margin !default;

$lv-cms-heading-2-font-size: $heading-2-font-size !default;
$lv-cms-heading-2-line-height: $heading-2-line-height !default;
$lv-cms-heading-2-font-weight: $heading-2-font-weight !default;
$lv-cms-heading-2-margin: $heading-2-margin !default;

$lv-cms-heading-3-font-size: $heading-3-font-size !default;
$lv-cms-heading-3-line-height: $heading-3-line-height !default;
$lv-cms-heading-3-font-weight: $heading-3-font-weight !default;
$lv-cms-heading-3-margin: $heading-3-margin !default;

$lv-cms-heading-4-font-size: $heading-4-font-size !default;
$lv-cms-heading-4-line-height: $heading-4-line-height !default;
$lv-cms-heading-4-font-weight: $heading-4-font-weight !default;
$lv-cms-heading-4-margin: $heading-4-margin !default;

$lv-cms-heading-5-font-size: $heading-5-font-size !default;
$lv-cms-heading-5-line-height: $heading-5-line-height !default;
$lv-cms-heading-5-font-weight: $heading-5-font-weight !default;
$lv-cms-heading-5-margin: $heading-5-margin !default;

$lv-cms-heading-6-font-size: $heading-6-font-size !default;
$lv-cms-heading-6-line-height: $heading-6-line-height !default;
$lv-cms-heading-6-font-weight: $heading-6-font-weight !default;
$lv-cms-heading-6-margin: $heading-6-margin !default;

$lv-cms-heading-gutter-clean-value: $heading-gutter-clean-value !default;

$cms-paragraph-gutter-size-sm: $gutter-size-sm !default;
$cms-paragraph-gutter-size-md: $gutter-size-md !default;
$cms-paragraph-gutter-size-xs: $gutter-size-xs !default;
$cms-paragraph-font-size: $paragraph-font-size !default;
$cms-paragraph-line-height-mobile: $paragraph-line-height-mobile !default;
$cms-paragraph-line-height-tablet: $paragraph-line-height-tablet !default;

$cms-list-font-size: $list-font-size !default;
$cms-list-line-height: $list-line-height !default;
$cms-list-gutter-size-sm: $gutter-size-sm !default;
$cms-list-gutter-size-lg: $gutter-size-lg !default;
$cms-list-li-display: $list-li-display !default;
$cms-ul-list-style-type: $list-ul-style-type !default;
$cms-ol-list-style-type: $list-ol-style-type !default;

$cms-ck-image-style-spacing: 1.5em !default;
$cms-a-color: $color-brand-primary !default;
$cms-a-active-hover-focus-color: $color-brand-primary !default;
$cms-a-visited-color: $color-brand-secondary !default;

$cms-blockquote-border-color: $color-cms-nobel !default;
$cms-image-figcaption-color: $color-mine-shaft !default;
$cms-image-figcaption-background: $color-alabaster !default;
$cms-table-border-color: $color-cms-dark-nobel !default;
$cms-table-td-border-color: $color-cms-alto !default;
$cms-table-th-border-color: $color-cms-alto !default;
$cms-table-th-background: $color-cms-alabaster !default;

$cms-video-player-ratio: percentage(math.div(9, 16)) !default;

$cms-social-instagram-ratio: percentage(math.div(9, 16)) !default;

$cms-audio-player-height: percentage(math.div(1, 8)) !default;
$cms-audio-player-height-spotify: 80px !default;

$cms-embed-generic-padding: 20px 40px !default;
$cms-embed-generic-border-radius: 4px !default;

$cms-embed-generic-icon-default: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 64 42'%3E%3Cpath d='M47.426 17V3.713L63.102 0v19.389h-.001l.001.272c0 1.595-2.032 3.43-4.538 4.098-2.506.668-4.538-.083-4.538-1.678 0-1.594 2.032-3.43 4.538-4.098.914-.244 2.032-.565 2.888-.603V4.516L49.076 7.447v9.556A1.014 1.014 0 0049 17h-1.574zM29.5 17h-8.343a7.073 7.073 0 10-4.657 4.06v3.781H3.3a2.803 2.803 0 01-2.8-2.804V8.63a2.803 2.803 0 012.8-2.805h4.082L8.58 2.768A1.994 1.994 0 0110.435 1.5h8.985c.773 0 1.477.448 1.805 1.149l1.488 3.177H26.7c1.546 0 2.8 1.256 2.8 2.805V17zm-11.637 0H17.5a1 1 0 00-1 1v.05A4.244 4.244 0 1117.863 17zm29.684 2c.97 0 .953-.048.953.889v20.743c0 .953.016.905-.953.905H19.453c-.97 0-.953.048-.953-.905V19.89c0-.937-.016-.889.97-.889h28.077zm-4.701 19.338V22.183H24.154v16.155h18.692zM20.6 21.375v1.616h1.616v-1.616H20.6zm0 3.231v1.616h1.616v-1.616H20.6zm0 3.231v1.616h1.616v-1.616H20.6zm0 3.231v1.616h1.616v-1.616H20.6zm0 3.231v1.616h1.616v-1.616H20.6zm0 3.231v1.616h1.616V37.53H20.6zm24.233-16.155v1.616h1.615v-1.616h-1.615zm0 3.231v1.616h1.615v-1.616h-1.615zm0 3.231v1.616h1.615v-1.616h-1.615zm0 3.231v1.616h1.615v-1.616h-1.615zm0 3.231v1.616h1.615v-1.616h-1.615zm0 3.231v1.616h1.615V37.53h-1.615zM29.485 25.283a.4.4 0 01.593-.35l9.05 4.977a.4.4 0 010 .701l-9.05 4.978a.4.4 0 01-.593-.35v-9.956z'/%3E%3C/svg%3E") !default;
$cms-embed-generic-icon-instagram: url("data:image/svg+xml;base64,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") !default;
$cms-embed-generic-icon-facebook: url("data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAyNCIgaGVpZ2h0PSIxMDI0IiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPjxwYXRoIGQ9Ik05NjcuNDg0IDBINTYuNTE3QzI1LjMwNCAwIDAgMjUuMzA0IDAgNTYuNTE3djkxMC45NjZDMCA5OTguNjk0IDI1LjI5NyAxMDI0IDU2LjUyMiAxMDI0SDU0N1Y2MjhINDE0VjQ3M2gxMzNWMzU5LjAyOWMwLTEzMi4yNjIgODAuNzczLTIwNC4yODIgMTk4Ljc1Ni0yMDQuMjgyIDU2LjUxMyAwIDEwNS4wODYgNC4yMDggMTE5LjI0NCA2LjA4OVYyOTlsLTgxLjYxNi4wMzdjLTYzLjk5MyAwLTc2LjM4NCAzMC40OTItNzYuMzg0IDc1LjIzNlY0NzNoMTUzLjQ4N2wtMTkuOTg2IDE1NUg3MDd2Mzk2aDI2MC40ODRjMzEuMjEzIDAgNTYuNTE2LTI1LjMwMyA1Ni41MTYtNTYuNTE2VjU2LjUxNUMxMDI0IDI1LjMwMyA5OTguNjk3IDAgOTY3LjQ4NCAwIiBmaWxsPSIjRkZGRkZFIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiLz48L3N2Zz4=") !default;
$cms-embed-generic-icon-google-maps: url("data:image/svg+xml;base64,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") !default;
$cms-embed-generic-icon-flickr: false !default;

$cms-embed-generic-background-default: $color-concrete !default;
$cms-embed-generic-background-instagram: linear-gradient(-135deg, #1400C7, #B800B1, #F50000) !default;
$cms-embed-generic-background-facebook: $color-facebook-light !default;
$cms-embed-generic-background-google-maps: false !default;
$cms-embed-generic-background-flickr: false !default;

$cms-embed-generic-color-default: $color-emperor !default;
$cms-embed-generic-color-instagram: $color-white !default;
$cms-embed-generic-color-facebook: $color-white !default;
$cms-embed-generic-color-google-maps: false !default;
$cms-embed-generic-color-flickr: false !default;

.cms {
  h1 {
    font-size: $lv-cms-heading-1-font-size;
    font-weight: $lv-cms-heading-1-font-weight;
    line-height: $lv-cms-heading-1-line-height;
    margin: $lv-cms-heading-1-margin;
  }

  h2 {
    font-size: $lv-cms-heading-2-font-size;
    font-weight: $lv-cms-heading-2-font-weight;
    line-height: $lv-cms-heading-2-line-height;
    margin: $lv-cms-heading-2-margin;
  }

  h3 {
    font-size: $lv-cms-heading-3-font-size;
    font-weight: $lv-cms-heading-3-font-weight;
    line-height: $lv-cms-heading-3-line-height;
    margin: $lv-cms-heading-3-margin;
  }

  h4 {
    font-size: $lv-cms-heading-4-font-size;
    font-weight: $lv-cms-heading-4-font-weight;
    line-height: $lv-cms-heading-4-line-height;
    margin: $lv-cms-heading-4-margin;
  }

  h5 {
    font-size: $lv-cms-heading-5-font-size;
    font-weight: $lv-cms-heading-5-font-weight;
    line-height: $lv-cms-heading-5-line-height;
    margin: $lv-cms-heading-5-margin;
  }

  h6 {
    font-size: $lv-cms-heading-6-font-size;
    font-weight: $lv-cms-heading-6-font-weight;
    line-height: $lv-cms-heading-6-line-height;
    margin: $lv-cms-heading-6-margin;
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    display: $lv-cms-heading-display;

    &:first-child {
      margin-top: $lv-cms-heading-gutter-clean-value;
    }

    &:last-child {
      margin-bottom: $lv-cms-heading-gutter-clean-value;
    }
  }

  p {
    @include md() {
      line-height: $cms-paragraph-line-height-tablet;
      margin: $cms-paragraph-gutter-size-sm 0;
    }

    @include lg() {
      margin: $cms-paragraph-gutter-size-md 0;
    }

    font-size: $cms-paragraph-font-size;
    line-height: $cms-paragraph-line-height-mobile;
    margin: $cms-paragraph-gutter-size-xs 0;

    &:first-child {
      margin-top: 0;
    }

    &:last-child {
      margin-bottom: 0;
    }
  }

  ul,
  ol {
    font-size: $cms-list-font-size;
    line-height: $cms-list-line-height;
    margin: $cms-list-gutter-size-sm 0;
    padding-left: $cms-list-gutter-size-lg;

    li {
      display: $cms-list-li-display;
    }
  }

  ul {
    list-style-type: $cms-ul-list-style-type;
  }

  ol {
    list-style-type: $cms-ol-list-style-type;
  }

  a {
    font-size: 16px;
    line-height: 19px;
    cursor: pointer;
    color: $cms-a-color;

    &:active,
    &:focus,
    &:hover {
      text-decoration: underline;
      color: $cms-a-active-hover-focus-color;
    }

    &:visited {
      text-decoration: none;
      color: $cms-a-visited-color;
    }
  }

  blockquote {
    font-style: italic;
    overflow: hidden;
    margin-right: 0;
    margin-left: 0;
    padding-right: 1.5em;
    padding-left: 1.5em;
    border-left: 5px solid $cms-blockquote-border-color;
  }

  &[dir=rtl] blockquote {
    border-right: 5px solid $cms-blockquote-border-color;
    border-left: 0;
  }

  :global {
    .image {
      display: table;
      clear: both;
      margin: 1em auto;
      text-align: center;

      > {
        img {
          display: block;
          min-width: 50px;
          max-width: 100%;
          margin: 0 auto;
        }

        figcaption {
          font-size: .75em;
          display: table-caption;
          padding: .6em;
          caption-side: bottom;
          word-break: break-word;
          color: $cms-image-figcaption-color;
          outline-offset: -1px;
          background: $cms-image-figcaption-background;
        }
      }
    }

    .image-style-align-center:not(.image_resized),
    .image-style-align-left:not(.image_resized),
    .image-style-align-right:not(.image_resized) {
      max-width: 50%;
    }

    .image-style-side {
      &:not(.image_resized) {
        max-width: 50%;
      }

      float: right;
      margin-left: $cms-ck-image-style-spacing;
    }

    .image-style-align-left {
      float: left;
      margin-right: $cms-ck-image-style-spacing;
    }

    .image-style-align-center {
      margin-right: auto;
      margin-left: auto;
    }

    .image-style-align-right {
      float: right;
      margin-left: $cms-ck-image-style-spacing;
    }

    .image.image_resized {
      display: block;
      box-sizing: border-box;
      max-width: 100%;

      img {
        width: 100%;
      }

      > figcaption {
        display: block;
      }
    }

    .media {
      display: block;
      clear: both;
      min-width: 15em;
      margin: 1em 0;
    }

    .table {
      display: block;
      overflow: auto;
      max-width: 100%;
      margin: 1em auto;

      table {
        border-spacing: 0;
        border-collapse: collapse;
        border: 1px double $cms-table-border-color;

        td {
          min-width: 2em;
          padding: .4em;
          border-color: $cms-table-td-border-color;
        }

        th {
          font-weight: 700;
          min-width: 2em;
          padding: .4em;
          border-color: $cms-table-th-border-color;
          background: $cms-table-th-background;
        }
      }
    }

    .audio {
      position: relative;
      padding-bottom: $cms-audio-player-height;

      iframe {
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        width: 100%;
        height: 100%;
        border: none;
      }

      & + & {
        @include md() {
          margin-top: 20px;
        }

        margin-top: 10px;
      }

      &.spotify {
        padding-bottom: $cms-audio-player-height-spotify;
      }
    }

    .video {
      position: relative;
      padding-bottom: $cms-video-player-ratio;

      iframe {
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        width: 100%;
        height: 100%;
        border: none;
      }

      & + & {
        @include md() {
          margin-top: 20px;
        }

        margin-top: 10px;
      }
    }

    .social {
      &.instagram {
        position: relative;
        padding-bottom: $cms-social-instagram-ratio;

        iframe {
          position: absolute;
          top: 0;
          right: 0;
          bottom: 0;
          left: 0;
          width: 100%;
          height: 100%;
          border: none;
        }

        & + & {
          @include md() {
            margin-top: 20px;
          }

          margin-top: 10px;
        }
      }
    }

    .embed-generic {
      display: block;
      overflow: hidden;
      padding: $cms-embed-generic-padding;
      text-align: center;
      border-radius: $cms-embed-generic-border-radius;

      @if ($cms-embed-generic-background-default) {
        background: $cms-embed-generic-background-default;
      }

      span {
        font-size: 13px;
        font-style: italic;
        display: inline-block;
        overflow: hidden;
        max-width: 100%;
        vertical-align: middle;
        white-space: nowrap;
        text-overflow: ellipsis;

        @if ($cms-embed-generic-color-default) {
          color: $cms-embed-generic-color-default;
        }

        &::before {
          @if ($cms-embed-generic-icon-default) {
            content: "";
          }
          font-size: 40px;
          line-height: 1.25;
          display: block;

          width: 55px;
          height: 40px;
          margin: auto auto 15px;
          text-align: center;
          background-image: $cms-embed-generic-icon-default;
          background-repeat: no-repeat;
          background-position: center center;
          background-size: contain;
        }
      }

      &:hover,
      &:active,
      &:focus {
        text-decoration: none;
      }

      &.flickr {
        @if ($cms-embed-generic-background-flickr) {
          background: $cms-embed-generic-background-flickr;
        }

        span {
          @if ($cms-embed-generic-color-flickr) {
            color: $cms-embed-generic-color-flickr;
          }

          @if ($cms-embed-generic-icon-flickr) {
            &::before {
              @if ($cms-embed-generic-icon-default == false) {
                content: "";
              }

              background-image: $cms-embed-generic-icon-flickr;
            }
          }
        }
      }

      &.instagram {
        @if ($cms-embed-generic-background-instagram) {
          background: $cms-embed-generic-background-instagram;
        }

        span {
          @if ($cms-embed-generic-color-instagram) {
            color: $cms-embed-generic-color-instagram;
          }

          @if ($cms-embed-generic-icon-instagram) {
            &::before {
              @if ($cms-embed-generic-icon-default == false) {
                content: "";
              }

              background-image: $cms-embed-generic-icon-instagram;
            }
          }
        }
      }

      &.facebook {
        @if ($cms-embed-generic-background-facebook) {
          background: $cms-embed-generic-background-facebook;
        }

        span {
          @if ($cms-embed-generic-color-facebook) {
            color: $cms-embed-generic-color-facebook;
          }

          @if ($cms-embed-generic-icon-facebook) {
            &::before {
              @if ($cms-embed-generic-icon-default == false) {
                content: "";
              }

              background-image: $cms-embed-generic-icon-facebook;
            }
          }
        }
      }

      &.googleMaps {
        @if ($cms-embed-generic-background-google-maps) {
          background: $cms-embed-generic-background-google-maps;
        }

        span {
          @if ($cms-embed-generic-color-google-maps) {
            color: $cms-embed-generic-color-google-maps;
          }

          @if ($cms-embed-generic-icon-google-maps) {
            &::before {
              @if ($cms-embed-generic-icon-default == false) {
                content: "";
              }

              background-image: $cms-embed-generic-icon-google-maps;
            }
          }
        }
      }
    }
  }

  &Version2 {
    font-family: $secondary-font;
    font-weight: $font-weight-normal-v2;
    line-height: 24px;

    h1 {
      font-size: $heading-1-font-size-mobile-version2;
      font-weight: $heading-1-font-weight-mobile-version2;
      line-height: $heading-1-line-height-mobile-version2;
      margin: $heading-1-margin-mobile-version2;
    }

    h2 {
      font-size: $heading-2-font-size-mobile-version2;
      font-weight: $heading-2-font-weight-mobile-version2;
      line-height: $heading-2-line-height-mobile-version2;
      margin: $heading-2-margin-mobile-version2;
    }

    h3 {
      font-size: $heading-3-font-size-mobile-version2;
      font-weight: $heading-3-font-weight-mobile-version2;
      line-height: $heading-3-line-height-mobile-version2;
      margin: $heading-3-margin-mobile-version2;
    }

    h4 {
      font-size: $heading-4-font-size-mobile-version2;
      font-weight: $heading-4-font-weight-mobile-version2;
      line-height: $heading-4-line-height-mobile-version2;
      margin: $heading-4-margin-mobile-version2;
    }

    @include tablet(min) {
      h1 {
        font-size: $heading-1-font-size-desktop-version2;
        font-weight: $heading-1-font-weight-desktop-version2;
        line-height: $heading-1-line-height-desktop-version2;
        margin: $heading-1-margin-desktop-version2;
      }

      h2 {
        font-size: $heading-2-font-size-desktop-version2;
        font-weight: $heading-2-font-weight-desktop-version2;
        line-height: $heading-2-line-height-desktop-version2;
        margin: $heading-2-margin-desktop-version2;
      }

      h3 {
        font-size: $heading-3-font-size-desktop-version2;
        font-weight: $heading-3-font-weight-desktop-version2;
        line-height: $heading-3-line-height-desktop-version2;
        margin: $heading-3-margin-desktop-version2;
      }

      h4 {
        font-size: $heading-4-font-size-desktop-version2;
        font-weight: $heading-4-font-weight-desktop-version2;
        line-height: $heading-4-line-height-desktop-version2;
        margin: $heading-4-margin-desktop-version2;
      }
    }

    a {
      font-size: 16px;
      font-weight: $font-weight-medium-v2;
      line-height: 24px;
      cursor: pointer;
      color: $color-text-primary;

      &:active,
      &:focus,
      &:hover {
        text-decoration: underline;
        color: $color-text-primary;
      }

      &:visited {
        text-decoration: none;
        color: $color-text-secondary;

        &:hover {
          text-decoration: underline;
          color: $color-button-hover;
        }
      }
    }

    strong, b {
      font-weight: $font-weight-bold-v2;
    }
  }
}
</style>
